import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { ZoningMap } from '@modules/lead-details/ZoningMap';
import { GOOGLE_MAPS_MAP_ID } from '@shared/config';
import { useLocale } from '@shared/hooks/locale';

import { type MapsApiWithLibraries } from '../../hooks/useGoogleMapsApi';

export const SATELLITE_ZOOM = 19;
export const PREVIEW_SATELLITE_ZOOM = 14;
export const INSET_ZOOM = 10;
export const SATELLITE_3D_RANGE = 86;
export const PREVIEW_SATELLITE_3D_RANGE = 900;
export const SATELLITE_3D_TILT = 38;
export const SATELLITE_3D_MARKER_ALTITUDE = 1;
export const SATELLITE_3D_ORBIT_DURATION_MS = 15000;

type Map3DElement = HTMLElement & {
  flyCameraAround?: (options: Record<string, unknown>) => Promise<void>;
  flyCameraTo?: (options: Record<string, unknown>) => Promise<void>;
  stopCameraAnimation?: () => Promise<void>;
};

type Maps3DLibrary = {
  Map3DElement: new (options: Record<string, unknown>) => Map3DElement;
  Marker3DElement?: new (options: Record<string, unknown>) => HTMLElement;
};

type AdvancedMarker = {
  map: google.maps.Map | null;
};

type MapsApiWithMarkerLibrary = MapsApiWithLibraries & {
  marker?: {
    AdvancedMarkerElement?: new (options: {
      map: google.maps.Map;
      position: google.maps.LatLngLiteral;
    }) => AdvancedMarker;
  };
};

type MarkerEffectProps = {
  mapsApi: typeof google.maps;
  map: google.maps.Map | null;
  position: google.maps.LatLngLiteral;
  enabled: boolean;
};

export const usePropertyMapMarker = ({
  mapsApi,
  map,
  position,
  enabled,
}: MarkerEffectProps) => {
  const { lat, lng } = position;

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    const createMarker = async () => {
      if (!enabled || !map) {
        return;
      }

      const mapsApiWithMarkerLibrary = mapsApi as MapsApiWithMarkerLibrary;
      const markerPosition = { lat, lng };

      if (
        GOOGLE_MAPS_MAP_ID &&
        mapsApiWithMarkerLibrary.importLibrary != null
      ) {
        await mapsApiWithMarkerLibrary.importLibrary('marker');
      }

      if (cancelled) {
        return;
      }

      const AdvancedMarkerElement =
        mapsApiWithMarkerLibrary.marker?.AdvancedMarkerElement;

      if (GOOGLE_MAPS_MAP_ID && AdvancedMarkerElement != null) {
        const marker = new AdvancedMarkerElement({
          map,
          position: markerPosition,
        });

        cleanup = () => {
          marker.map = null;
        };
        return;
      }

      const marker = new mapsApi.Marker({
        map,
        position: markerPosition,
      });

      cleanup = () => {
        marker.setMap(null);
      };
    };

    createMarker();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [enabled, lat, lng, map, mapsApi]);
};

type ZoningMapPaneProps = {
  lat: number;
  lng: number;
};

export const ZoningMapPane = ({ lat, lng }: ZoningMapPaneProps) => {
  return <ZoningMap lat={lat} lng={lng} zoom={17} />;
};

type StreetViewPaneProps = {
  lat: number;
  lng: number;
  mapsApi: typeof google.maps;
  panoramaData: google.maps.StreetViewPanoramaData | null;
};

export const StreetViewPane = ({
  lat,
  lng,
  mapsApi,
  panoramaData,
}: StreetViewPaneProps) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>(
    panoramaData ? 'loading' : 'unavailable',
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useLocale();

  useEffect(() => {
    if (!containerRef.current || !panoramaData?.location) {
      setStatus('unavailable');
      return;
    }

    const panoramaPosition =
      panoramaData.location.latLng ?? new mapsApi.LatLng(lat, lng);

    const panorama = new mapsApi.StreetViewPanorama(containerRef.current, {
      pano: panoramaData.location.pano,
      position: panoramaPosition,
      motionTracking: false,
      motionTrackingControl: false,
      clickToGo: true,
      addressControl: true,
    });

    setStatus('ready');

    return () => {
      panorama.setVisible(false);
    };
  }, [lat, lng, mapsApi, panoramaData]);

  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      <Box ref={containerRef} sx={{ height: '100%' }} />

      {status !== 'ready' && (
        <Box
          sx={theme => ({
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.85)',
            pointerEvents: 'none',
            ...theme.applyStyles('dark', {
              bgcolor: 'rgba(8,10,14,0.85)',
            }),
          })}
        >
          <Typography variant="body2">
            {status === 'unavailable'
              ? t('Street View is not available for this location.')
              : t('Loading street view...')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

type Satellite3DPaneProps = {
  lat: number;
  lng: number;
  mapsApi: typeof google.maps;
  readOnly: boolean;
  showMarker: boolean;
  fallback: React.ReactNode;
};

export const Satellite3DPane = ({
  lat,
  lng,
  mapsApi,
  readOnly,
  showMarker,
  fallback,
}: Satellite3DPaneProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useLayoutEffect(() => {
    setUnavailable(false);
  }, [lat, lng, mapsApi, readOnly, showMarker]);

  useEffect(() => {
    let cancelled = false;
    let cleanupUserInteractionListeners = () => {};
    let mapElement: Map3DElement | null = null;

    const initializeMap = async () => {
      if (!containerRef.current) {
        return;
      }

      const mapsApiWithLibraries = mapsApi as MapsApiWithLibraries;

      if (!mapsApiWithLibraries.importLibrary) {
        setUnavailable(true);
        return;
      }

      try {
        const { Map3DElement, Marker3DElement } =
          (await mapsApiWithLibraries.importLibrary(
            'maps3d',
          )) as unknown as Maps3DLibrary;

        if (cancelled || !containerRef.current) {
          return;
        }

        const range = readOnly
          ? PREVIEW_SATELLITE_3D_RANGE
          : SATELLITE_3D_RANGE;

        mapElement = new Map3DElement({
          center: { lat, lng, altitude: 0 },
          range,
          tilt: SATELLITE_3D_TILT,
          heading: 0,
          mode: 'HYBRID',
          gestureHandling: readOnly ? 'COOPERATIVE' : 'GREEDY',
        });
        mapElement.style.width = '100%';
        mapElement.style.height = '100%';
        mapElement.style.pointerEvents = readOnly ? 'none' : 'auto';

        containerRef.current.replaceChildren(mapElement);

        await mapElement.flyCameraTo?.({
          endCamera: {
            center: { lat, lng, altitude: 0 },
            altitudeMode: 'RELATIVE_TO_GROUND',
            range,
            tilt: SATELLITE_3D_TILT,
            heading: 0,
          },
          durationMillis: 0,
        });

        if (cancelled || !containerRef.current) {
          return;
        }

        if (showMarker && Marker3DElement) {
          const marker = new Marker3DElement({
            position: { lat, lng, altitude: SATELLITE_3D_MARKER_ALTITUDE },
            altitudeMode: 'RELATIVE_TO_MESH',
            drawsWhenOccluded: true,
            sizePreserved: true,
          });
          mapElement.append(marker);
        }

        const canOrbit =
          !readOnly &&
          showMarker &&
          mapElement.flyCameraAround != null &&
          mapElement.stopCameraAnimation != null;

        if (canOrbit) {
          const stopOrbit = () => {
            cleanupUserInteractionListeners();
            void mapElement?.stopCameraAnimation?.();
          };

          mapElement.addEventListener('pointerdown', stopOrbit);
          mapElement.addEventListener('click', stopOrbit);
          mapElement.addEventListener('wheel', stopOrbit);

          cleanupUserInteractionListeners = () => {
            mapElement?.removeEventListener('pointerdown', stopOrbit);
            mapElement?.removeEventListener('click', stopOrbit);
            mapElement?.removeEventListener('wheel', stopOrbit);
          };

          void mapElement
            .flyCameraAround?.({
              camera: {
                center: { lat, lng, altitude: 0 },
                altitudeMode: 'RELATIVE_TO_GROUND',
                range,
                tilt: SATELLITE_3D_TILT,
                heading: 0,
              },
              durationMillis: SATELLITE_3D_ORBIT_DURATION_MS,
              rounds: -1,
            })
            ?.catch(() => {
              cleanupUserInteractionListeners();
            });
        }
      } catch {
        if (!cancelled) {
          setUnavailable(true);
        }
      }
    };

    initializeMap();

    return () => {
      cancelled = true;
      cleanupUserInteractionListeners();
      mapElement?.remove();
    };
  }, [lat, lng, mapsApi, readOnly, showMarker]);

  if (unavailable) {
    return fallback;
  }

  return <Box ref={containerRef} sx={{ height: '100%', width: '100%' }} />;
};

type TwoDimensionalMapPaneProps = {
  center: google.maps.LatLngLiteral;
  defaultZoom: number;
  inset?: boolean;
  mapTypeId: google.maps.MapTypeId | string;
  mapsApi: typeof google.maps;
  readOnly: boolean;
  showMarker: boolean;
  controlSize?: number;
  /** Vector-map pan/zoom cluster (bottom-right). Off on lead inset previews. */
  showCameraControl?: boolean;
};

export const TwoDimensionalMapPane = ({
  center,
  defaultZoom,
  inset = false,
  mapTypeId,
  mapsApi,
  readOnly,
  showMarker,
  controlSize,
  showCameraControl = true,
}: TwoDimensionalMapPaneProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const { lat, lng } = center;

  useEffect(() => {
    if (!elementRef.current) {
      return;
    }

    const controlPosition = (
      mapsApi as {
        ControlPosition?: typeof google.maps.ControlPosition;
      }
    ).ControlPosition;

    const showNativeControls = !inset && !readOnly;
    const showCamera = showNativeControls && showCameraControl;

    const options = {
      center: { lat, lng },
      zoom: defaultZoom,
      mapTypeId,
      mapTypeControl: false,
      clickableIcons: false,
      disableDefaultUI: inset || readOnly,
      gestureHandling: inset || readOnly ? 'none' : 'greedy',
      draggable: !(inset || readOnly),
      cameraControl: showCamera,
      zoomControl: false,
      streetViewControl: false,
      fullscreenControl: showNativeControls,
      rotateControl: showNativeControls,
      ...(controlSize != null && { controlSize }),
      ...(controlPosition?.RIGHT_TOP &&
        showNativeControls && {
          rotateControlOptions: {
            position: controlPosition.RIGHT_TOP,
          },
          fullscreenControlOptions: {
            position: controlPosition.TOP_RIGHT,
          },
        }),
      ...(controlPosition?.RIGHT_BOTTOM &&
        showCamera && {
          cameraControlOptions: {
            position: controlPosition.RIGHT_BOTTOM,
          },
        }),
      ...(GOOGLE_MAPS_MAP_ID && { mapId: GOOGLE_MAPS_MAP_ID }),
      ...(readOnly && {
        scrollwheel: false,
        disableDoubleClickZoom: true,
        minZoom: defaultZoom,
        maxZoom: defaultZoom,
      }),
    } as google.maps.MapOptions;

    const nextMap = new mapsApi.Map(elementRef.current, options);
    setMap(nextMap);

    return () => {
      setMap(null);
    };
    // `mapTypeId` is intentionally excluded: switching map/satellite updates the
    // existing instance via `setMapTypeId` below instead of recreating the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    controlSize,
    defaultZoom,
    inset,
    lat,
    lng,
    mapsApi,
    readOnly,
    showCameraControl,
  ]);

  useEffect(() => {
    if (map) {
      map.setMapTypeId(mapTypeId);
    }
  }, [map, mapTypeId]);

  usePropertyMapMarker({
    mapsApi,
    map,
    position: center,
    enabled: showMarker,
  });

  return <Box ref={elementRef} sx={{ height: '100%', width: '100%' }} />;
};
